import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@features/auth/AuthProvider';
import { OtpVerificationView } from '@features/auth/OtpVerificationView';
import { SetPasswordView } from '@features/auth/SetPasswordView';
import { Button } from '@shared/ui/button';
import { Input } from '@shared/ui/input';
import { extractApiErrorMessage } from '@shared/lib/extractApiErrorMessage';
export const ForgotPasswordView = ({ initialIdentifier = '', onBack, onSuccess, }) => {
    const { t } = useTranslation('auth');
    const { forgotPassword, resetPassword } = useAuth();
    const [step, setStep] = useState('identifier');
    const [identifier, setIdentifier] = useState(initialIdentifier);
    const [resetCode, setResetCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const autoSentRef = useRef(false);
    const handleRequestReset = async () => {
        if (!identifier.trim()) {
            toast.error(t('toasts.otpRequestUserRequired'));
            return;
        }
        setIsSubmitting(true);
        try {
            await forgotPassword(identifier.trim());
            setStep('otp');
            toast.success(t('forgotPassword.sent'));
        }
        catch (error) {
            toast.error(extractApiErrorMessage(error) ?? t('toasts.otpSendError'));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    useEffect(() => {
        if (initialIdentifier.trim() && !autoSentRef.current) {
            autoSentRef.current = true;
            void handleRequestReset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (step === 'identifier') {
        return (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "text-center space-y-2", children: [_jsx("h2", { className: "text-2xl font-bold text-white", children: t('forgotPassword.title') }), _jsx("p", { className: "text-sm text-muted-foreground", children: t('forgotPassword.subtitle') })] }), _jsx(Input, { value: identifier, onChange: (e) => setIdentifier(e.target.value), placeholder: t('form.usernamePlaceholder'), className: "h-14 bg-white/5 border-white/10 rounded-2xl", autoComplete: "username" }), _jsx(Button, { type: "button", onClick: handleRequestReset, disabled: isSubmitting, className: "w-full h-14 rounded-2xl font-bold", variant: "primary", children: isSubmitting ? t('buttons.processing') : t('forgotPassword.submit') }), _jsx("button", { type: "button", onClick: onBack, className: "w-full text-sm text-muted-foreground", children: t('buttons.backToQuickOptions') })] }));
    }
    if (step === 'otp') {
        return (_jsxs("div", { className: "space-y-4", children: [_jsx(OtpVerificationView, { identifier: identifier, onVerify: async (code) => {
                        setResetCode(code);
                        setStep('password');
                    }, onResend: handleRequestReset }), _jsx("button", { type: "button", onClick: onBack, className: "w-full text-sm text-muted-foreground", children: t('buttons.backToQuickOptions') })] }));
    }
    return (_jsxs("div", { className: "space-y-4", children: [_jsx(SetPasswordView, { onSubmit: async (password) => {
                    if (!resetCode) {
                        setStep('otp');
                        toast.error(t('forgotPassword.codeExpired'));
                        return;
                    }
                    try {
                        await resetPassword(identifier.trim(), resetCode, password);
                        toast.success(t('resetPassword.success'));
                        onSuccess?.();
                        onBack();
                    }
                    catch (error) {
                        toast.error(extractApiErrorMessage(error) ?? t('toasts.invalidOtp'));
                        throw error;
                    }
                } }), _jsx("button", { type: "button", onClick: onBack, className: "w-full text-sm text-muted-foreground", children: t('buttons.backToQuickOptions') })] }));
};
